import { Link } from "react-router-dom";
import { athletes as seedAthletes, type Athlete, type Cause } from "@/data/athletes";
import RichText from "@/components/RichText";
import { useSiteContent } from "@/hooks/useSiteContent";

type Item = Cause & { athleteNames: string[] };

const CausesSection = ({ athletes = seedAthletes }: { athletes?: Athlete[] }) => {
  const { t } = useSiteContent();

  // Dedupe causes appearing under multiple athletes by id (or name fallback for seeds).
  const map = new Map<string, Item>();
  athletes.forEach((a) => {
    a.causes.forEach((c) => {
      const key = c.id ?? c.name;
      const linked = c.athletes?.map((x) => x.name) ?? [a.name];
      const existing = map.get(key);
      if (existing) {
        linked.forEach((n) => { if (!existing.athleteNames.includes(n)) existing.athleteNames.push(n); });
      } else {
        map.set(key, { ...c, athleteNames: [...linked] });
      }
    });
  });
  const allCauses = Array.from(map.values());

  if (allCauses.length === 0) return null;

  const formatBacked = (names: string[]) => {
    if (names.length === 0) return "";
    if (names.length === 1) return `Backed by ${names[0]}`;
    if (names.length === 2) return `Backed by ${names[0]} & ${names[1]}`;
    return `Backed by ${names[0]} + ${names.length - 1} others`;
  };

  return (
    <section id="causes" className="py-24 bg-gradient-warm">
      <div className="container">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{t("causes.eyebrow", "Give back")}</span>
            <h2 className="text-5xl md:text-7xl mt-3 text-balance">{t("causes.heading", "Causes that matter to them.")}</h2>
          </div>
          <p className="text-muted-foreground max-w-sm">
            {t("causes.intro", "Every cause here is championed by an athlete on our roster.")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {allCauses.map((c, i) => {
            const isHtml = /<\/?[a-z][\s\S]*>/i.test(c.blurb);
            const Wrapper: any = c.id ? Link : "article";
            const wrapperProps = c.id ? { to: `/cause/${c.id}` } : {};
            return (
              <Wrapper
                key={`${c.id ?? c.name}-${i}`}
                {...wrapperProps}
                className="bg-card rounded-[1.5rem] p-6 shadow-card hover:shadow-bold transition-shadow flex flex-col block"
              >
                {c.logoUrl && (
                  <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden mb-4 flex items-center justify-center">
                    <img src={c.logoUrl} alt={`${c.name} logo`} className="w-full h-full object-contain" />
                  </div>
                )}
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {formatBacked(c.athleteNames)}
                </span>
                <h3 className="font-display text-2xl mt-2">{c.name}</h3>
                <div className="text-sm text-muted-foreground mt-2 flex-1 line-clamp-3">
                  {isHtml ? <RichText html={c.blurb} /> : <p>{c.blurb}</p>}
                </div>
                {c.id && (
                  <span className="mt-5 inline-block text-xs font-semibold uppercase tracking-wider text-primary">
                    Learn more →
                  </span>
                )}
              </Wrapper>
            );
          })}
          
        </div>
      </div>
    </section>
  );
};

export default CausesSection;
